import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';

const STORAGE_KEY = 'bedok_popup_monthly';
const MAX_PER_MONTH = 3;

function shouldShowPopup() {
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${now.getMonth()}`;
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    if (stored.month !== currentMonth) return true;
    return stored.count < MAX_PER_MONTH;
  } catch {
    return true;
  }
}

function recordPopupShown() {
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${now.getMonth()}`;
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    const count = stored.month === currentMonth ? stored.count + 1 : 1;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ month: currentMonth, count }));
  } catch {}
}

function AppleIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </SvgIcon>
  );
}

function GooglePlayIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M3.609 1.814L13.792 12 3.61 22.186a.5.5 0 0 1-.61-.484V2.298a.5.5 0 0 1 .609-.484zM14.5 12.707l2.313 2.313-8.408 4.784L14.5 12.707zm3.172-.059l1.534.872c.59.336.59 1.172 0 1.508l-1.534.872L15.207 12l2.465-2.352zM8.405 4.196l8.408 4.784L14.5 11.293 8.405 4.196z" />
    </SvgIcon>
  );
}

function StoreBadge({ Icon, topText, bottomText, href }) {
  return (
    <Button
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        flex: 1,
        backgroundColor: '#0d1a28',
        border: '1.5px solid #1e3550',
        borderRadius: 2.5,
        py: 1.2,
        px: 1.8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.2,
        textTransform: 'none',
        direction: 'ltr',
        minWidth: 0,
        transition: 'all 0.18s ease',
        '&:hover': {
          backgroundColor: '#122030',
          borderColor: '#00c090',
          boxShadow: '0 0 18px rgba(0,192,144,0.18)',
          transform: 'translateY(-1px)',
        },
        '&:active': { transform: 'translateY(0)' },
      }}
    >
      <Icon sx={{ fontSize: 26, color: '#fff', flexShrink: 0 }} />
      <Box sx={{ textAlign: 'left', lineHeight: 1 }}>
        <Typography
          component="span"
          sx={{ display: 'block', fontSize: '0.6rem', color: '#5a8aaa', letterSpacing: '0.03em', mb: '2px' }}
        >
          {topText}
        </Typography>
        <Typography
          component="span"
          sx={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#fff' }}
        >
          {bottomText}
        </Typography>
      </Box>
    </Button>
  );
}

function WeeklyPromoPopup() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!shouldShowPopup()) return;
    const timer = setTimeout(() => {
      setOpen(true);
      recordPopupShown();
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => setOpen(false);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 22,
          background: '#0b1420',
          border: '1px solid #182840',
          direction: 'rtl',
          overflow: 'hidden',
        },
      }}
    >
      {/* Top accent bar */}
      <Box sx={{ height: 3, background: 'linear-gradient(90deg, #00c090, #00a0d0)' }} />

      <Box sx={{ position: 'relative' }}>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            color: '#2e4860',
            '&:hover': { color: '#5a7a9a', backgroundColor: 'rgba(255,255,255,0.04)' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <DialogContent sx={{ pt: 3.5, pb: 3, px: 3, textAlign: 'center' }}>
          {/* Source badge */}
          <Typography
            sx={{
              display: 'inline-block',
              color: '#00c090',
              fontWeight: 700,
              fontSize: '0.68rem',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              mb: 2,
              opacity: 0.85,
            }}
          >
            IDFMedic
          </Typography>

          {/* Hook headline */}
          <Typography
            sx={{
              color: '#fff',
              fontWeight: 900,
              fontSize: '1.45rem',
              lineHeight: 1.35,
              mb: 1.2,
              textWrap: 'balance',
            }}
          >
            בדיקות כוננות באפליקציה —{' '}
            <span style={{ color: '#00c090' }}>הרבה יותר קל.</span>
          </Typography>

          {/* Action line */}
          <Typography
            sx={{
              color: '#c0d0e0',
              fontSize: '0.95rem',
              fontWeight: 600,
              mb: 2.5,
              lineHeight: 1.5,
            }}
          >
            עוברים לבדיקות כוננות וברה״צ דיגיטליות עם{' '}
            <span style={{ color: '#fff', fontWeight: 800 }}>בָּדוּק.</span>
          </Typography>

          {/* Audience fine print */}
          <Typography
            sx={{
              color: '#3a6080',
              fontSize: '0.78rem',
              mb: 3,
              lineHeight: 1.6,
            }}
          >
            מיועד לנגדי מרפאה · פתוח גם לחובשים ומט״בים{' '}
            <span style={{ color: '#c0d0e0', fontWeight: 600 }}>ללא צורך בהרשמה</span>
          </Typography>

          {/* Download label */}
          <Typography
            sx={{
              fontSize: '0.8rem',
              color: '#00c090',
              fontWeight: 700,
              letterSpacing: '0.12em',
              mb: 1,
            }}
          >
            הורדה חינם
          </Typography>

          {/* Store badges — primary CTA */}
          <Stack direction="column" spacing={1} sx={{ mb: 2 }}>
            <StoreBadge
              Icon={AppleIcon}
              topText="להורדה ב"
              bottomText="App Store"
              href="https://apps.apple.com/il/app/%D7%91%D7%93%D7%95%D7%A7/id6761129891"
            />
            <StoreBadge
              Icon={GooglePlayIcon}
              topText="להורדה ב"
              bottomText="Google Play"
              href="https://play.google.com/store/apps/details?id=com.bb787.baduk"
            />
          </Stack>

          {/* Secondary link */}
          <Typography
            component="a"
            href="https://landing.idfmedic.com/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'block',
              color: '#8ab0cc',
              fontSize: '0.85rem',
              fontWeight: 600,
              mb: 1.5,
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
              textDecorationColor: 'rgba(138,176,204,0.35)',
              transition: 'color 0.15s',
              '&:hover': { color: '#b0d0e8', textDecorationColor: 'rgba(176,208,232,0.6)' },
            }}
          >
            לנגדי מרפאה — פרטים נוספים
          </Typography>

          <Button
            onClick={handleClose}
            fullWidth
            sx={{
              color: '#223040',
              fontSize: '0.76rem',
              '&:hover': { color: '#3a5070', backgroundColor: 'transparent' },
            }}
          >
            לא עכשיו
          </Button>
        </DialogContent>
      </Box>
    </Dialog>
  );
}

export default WeeklyPromoPopup;
